// REACT
import React from "react";

// MATERIAL UI
import { Collapse, Grid, Typography, useTheme } from "@mui/material";

// COMPONENTS
import {
  getNumOfTransactionsThisMonth,
  useTransactionLowHigh,
} from "src/components/transactions/helperfns";
import Title from "src/components/common/Title";
import Filter from "src/components/transactions/Filter";
import GuestBanner from "src/components/dashboard/GuestBanner";
import { BrokerModal } from "src/components/core/BrokerHumanisation";
import FilterOptions from "src/components/transactions/FilterOptions";
import PageLayoutController from "src/components/core/PageLayoutController";
import TransactionTable from "src/components/transactions/TransactionTable";
import ComponentLayoutWrapper from "src/components/core/ComponentLayoutWrapper";
import { LoadingSpinner } from "src/components/application/LoadingSpinner";

// HOOKS
import usePermission from "src/utils/hooks/usePermission";
import useParamChange from "src/utils/hooks/useParamChange";

// REDUX
import {
  GetUserCurrencyTransactionsApiArg,
  PaginatedCTsRes,
} from "src/store/features/api/generated";
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";
import { addTransactionNote } from "src/store/features/transactions.slice";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// LAYOUTS
const DESKTOP_LAYOUT = [
  ["HEADING", "HEADING", "HEADING", "HEADING", "HEADING", "HEADING"],
  [".", ".", ".", "EXTRA", "EXTRA", "EXTRA"],
  ["FILTER", "FILTER", "FILTER", "FILTER", "FILTER", "FILTER"],
  ["TABLE", "TABLE", "TABLE", "TABLE", "TABLE", "TABLE"],
];

const MOBILE_LAYOUT = [[".", "."]];

export const DEFAULT_TRANSACTION_PARAMS: GetUserCurrencyTransactionsApiArg = {
  limit: 10,
  page: 1,
  baseCurrency: "",
  device: "desktop",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  txType: "all",
  currency: "",
  startDate: undefined,
  endDate: undefined,
  startTxSize: undefined,
  endTxSize: undefined,
};

const TransactionDesktop = () => {
  const theme = useTheme();
  const secondary = theme.palette.secondary.main;
  const [displayFilter, setDisplayFilter] = React.useState<boolean>(false);
  const [brokerOpen, setBrokerOpen] = React.useState<boolean>(false);
  const { userPermission } = usePermission();
  const dispatch = useAppDispatch();
  const handleBrokerModal = () => {
    setBrokerOpen((prev) => !prev);
  };

  const [
    currentParamState,
    setParamsCallback,
    handleParamReset,
    refetchDefault,
    setRefetchDefault,
  ] = useParamChange<GetUserCurrencyTransactionsApiArg>(DEFAULT_TRANSACTION_PARAMS);

  const {
    data: transactions,
    isFetching,
    isError,
    refetch,
  } = api.useGetUserCurrencyTransactionsQuery(currentParamState, { pollingInterval: 360000 }) as {
    data: PaginatedCTsRes;
    isFetching: boolean;
    isError: boolean;
    refetch: any;
  };

  // Add transaction notes to the redux
  React.useEffect(() => {
    if (transactions) {
      transactions.data.forEach((tx) => {
        if (tx.note) {
          dispatch(addTransactionNote({ id: tx.id, note: tx.note }));
        }
      });
    }
  }, [transactions]);

  // something wierd happens were if i reset the currentParamState in useParamChange, it does not fetch.
  // have to forceably refetch by using this useEffect
  React.useEffect((): void => {
    if (refetchDefault) {
      refetch();
      setTimeout((): void => {
        setRefetchDefault(false);
      }, 500);
    }
  }, [refetchDefault, refetch, setRefetchDefault]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setParamsCallback({ page: newPage });
  };

  const handleFilterChange = (event: React.MouseEvent<HTMLElement>, newAlignment: any) => {
    if (newAlignment) {
      setParamsCallback({ page: 1, txType: newAlignment });
      trackEvent(`Select_Tranasctions_Type`, {
        Transactions_Type: newAlignment.toUpperCase(),
      });
    }
  };

  /**
   * Returns a string indicating the number of transactions for the current month,
   * based on the `transactions` array passed as a prop or context.
   *
   * If the `transactions` array is empty, the function returns the string
   * "You have no transactions this month".
   *
   * If the `transactions` array has one or more items, the function returns
   * a string indicating the number of transactions, using the plural form "transactions"
   * if there are more than one.
   *
   * @returns A string indicating the number of transactions this month,
   * or a message indicating that there are no transactions
   */

  const transactionsThisMonth = (): string => {
    return getNumOfTransactionsThisMonth(transactions?.data) === 0
      ? `You have no transactions this month`
      : `You have ${getNumOfTransactionsThisMonth(transactions?.data)} transaction${
          getNumOfTransactionsThisMonth(transactions?.data) > 1 ? "s" : ""
        } this month`;
  };

  /**
   * A component that displays a message when there are no transactions to show, with a button to contact the user's broker for assistance.
   */
  const NoTransactionsText = (): JSX.Element => {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "200px" }}>
        {userPermission === "guest" ? (
          <Grid item xs={12}>
            <GuestBanner
              variant={2}
              descriptionPrefix="To view your transactions"
              featureName="transaction"
            />
          </Grid>
        ) : (
          <Grid item>
            Not seeing your transaction here? Speak to
            <button
              onClick={handleBrokerModal}
              style={{
                textDecoration: "underline",
                fontSize: "16px",
                color: secondary,
                border: "none",
                background: "none",
                cursor: "pointer",
              }}>
              your broker
            </button>
            for further assistance.
          </Grid>
        )}
      </Grid>
    );
  };

  const lowHigh = useTransactionLowHigh(transactions);

  return (
    <>
      <PageLayoutController
        mobileLayout={MOBILE_LAYOUT}
        desktopLayout={DESKTOP_LAYOUT}
        spacing={15}
        container={true}>
        {/* HEADING */}
        <ComponentLayoutWrapper
          gridArea="HEADING"
          state={{ isLoading: false }}
          fadeInDirection="none"
          loadingComponent={<></>}>
          <Title title={{ partB: "History" }} />
        </ComponentLayoutWrapper>

        {/* EXTRA */}
        <ComponentLayoutWrapper
          gridArea="EXTRA"
          state={{ isLoading: false }}
          fadeInDirection="none"
          loadingComponent={<></>}>
          <Grid container justifyContent="flex-end" item>
            <Typography id="transactions-this-month-text">{transactionsThisMonth()}</Typography>
          </Grid>
        </ComponentLayoutWrapper>

        {/* FILTER */}
        <ComponentLayoutWrapper
          gridArea="FILTER"
          state={{ isLoading: false }}
          fadeInDirection="none"
          loadingComponent={<></>}>
          <>
            <Filter
              type={currentParamState.txType ?? ""}
              onChange={handleFilterChange}
              displayFilter={displayFilter}
              setDisplayFilter={setDisplayFilter}
            />
            <Collapse in={displayFilter}>
              <FilterOptions
                currentParams={currentParamState}
                refetch={handleParamReset}
                setParams={setParamsCallback}
                isError={isError}
                lowHigh={lowHigh}
              />
            </Collapse>
          </>
        </ComponentLayoutWrapper>

        {/* TABLE */}
        <ComponentLayoutWrapper
          gridArea="TABLE"
          state={{ isLoading: isFetching }}
          fadeInDirection="none"
          loadingComponent={
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "500px",
              }}>
              <LoadingSpinner size={60} />
            </div>
          }>
          {transactions && transactions?.data.length <= 0 ? (
            <NoTransactionsText />
          ) : (
            <TransactionTable transactions={transactions} onPageChange={handleChangePage} />
          )}
        </ComponentLayoutWrapper>
      </PageLayoutController>
      <BrokerModal open={brokerOpen} setOpen={setBrokerOpen} />
    </>
  );
};

export default TransactionDesktop;
